import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { setUpdateChecker } from '../lib/swUpdate.js';
import { subscribeModals, modalsState } from '../lib/overlayStack.js';

// PWA update handling. VitePWA registerType:'prompt' installs a newly-deployed service
// worker but leaves it WAITING — so an open tab keeps running the old chunks (and the
// browser keeps serving the old precached files) until SOMETHING activates the new SW.
// Without that, Chrome + Safari serve stale files until every tab of the origin closes:
// the "it won't update" complaint. This component owns the whole flow:
//   1. Registers the SW (moved here from main.jsx so registration + updates live in one
//      place). App only mounts this on the tenant/customer portals, so it stays OFF the
//      signup/admin hosts — where a controlling SW would eat the auth handoff.
//   2. Checks for a new deploy every few minutes AND whenever the tab regains focus /
//      visibility (an installed PWA has no browser reload — focus is the natural moment).
//   3. Arrived with an update already waiting → applies it immediately (a fresh load, so
//      no in-progress work to lose). A deploy DURING the session → a non-blocking banner,
//      so an agent mid-reply is never yanked out from under.
//   4. applyUpdate() skipWaiting + reloads via workbox's 'controlling' event — which only
//      fires reliably (esp. on Chrome) because vite.config sets clientsClaim:true; a timed
//      fallback reload covers any browser/build that misses controllerchange.
//   5. Exposes the check for a manual "Check for updates" (Settings → About) through
//      lib/swUpdate.js — and as window.__esCheckForUpdate(), as before.
const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const APPLY_RELOAD_FALLBACK_MS = 3000;
const EXPLICIT_CHECK_TIMEOUT_MS = 8000;
// An update found within this window of arrival is treated as "you just loaded onto a
// stale shell" → apply silently; later = an in-session deploy → offer it via the banner.
// Kept SHORT (Fable): the genuine already-waiting case fires needRefresh in the first
// microtask after register; a slower fresh precache (globPatterns = the whole build) can
// finish at t≈5-9s, by which point an agent may have started a reply — so anything past
// ~3s degrades to the banner instead of silently reloading over in-progress work.
const ARRIVAL_AUTO_APPLY_MS = 3 * 1000;

// How long the screen-reader announcement stays in its live region: read once, then cleared.
const ANNOUNCE_MS = 5000;

// The banner is a manual popover, shown into the top layer as it mounts — above the app and
// any modal already open (a <dialog> also lives there). It renders INSIDE the topmost modal
// dialog when one is open: the browser makes everything outside that dialog inert, a popover
// above it included, so the banner would show but not take a click, focus or a screen reader.
// Any modal change remounts it (keyed by the change): a fresh element, shown last, so on top
// again — focus on its button carries over to the new one. No popover support: a plain fixed
// banner (styles.css), still inside the modal.
//
// So the banner itself isn't a live region (each remount would be read out again). One
// visually-hidden polite region, always rendered, gets the text once when the banner first
// shows and is emptied after ANNOUNCE_MS. It too sits in the topmost modal — outside, it'd be
// inert and never read — and only moves (remounts) when that modal changes, empty by then.
function showOnTop(el) {
  if (el && typeof el.showPopover === 'function') {
    try { el.showPopover(); } catch { /* stays the plain fixed banner */ }
  }
}
const NO_MODALS = { top: null, seq: 0 };
const noModals = () => NO_MODALS;

export default function UpdatePrompt() {
  const { t } = useTranslation('common');
  const registrationRef = useRef(null);
  const arrivedAt = useRef(Date.now());
  const [registration, setRegistration] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const modals = useSyncExternalStore(subscribeModals, modalsState, noModals);
  const refocusBanner = useRef(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker
  } = useRegisterSW({
    onRegisteredSW(_swUrl, reg) {
      registrationRef.current = reg || null;
      setRegistration(reg || null);
    }
  });

  // Poll for a new deploy periodically AND whenever the tab regains focus/visibility (an
  // installed PWA has no browser reload). Cleaned up on unmount so a stray host flip can't
  // leave an orphaned update loop running (Fable).
  useEffect(() => {
    if (!registration) return undefined;
    const check = () => registration.update().catch(() => {});
    const id = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [registration]);

  // skipWaiting + reload. updateServiceWorker(true) messages the waiting SW and reloads on
  // workbox's 'controlling' event; the timer is a belt-and-suspenders reload for any
  // browser/build that misses controllerchange — by then the new SW is ACTIVE and serves
  // the new precache, so the reload lands on the new version regardless. If workbox already
  // reloaded, the page is gone and this timer dies with it.
  const applyUpdate = useCallback(() => {
    setTimeout(() => window.location.reload(), APPLY_RELOAD_FALLBACK_MS);
    updateServiceWorker(true);
  }, [updateServiceWorker]);

  // Imperative, user-initiated check — for a manual "check for updates" control (an
  // installed iOS PWA has no browser reload, so this is the only way to force new code).
  // Resolves 'updated' | 'current' | 'unsupported' | 'reloading'.
  const checkForUpdate = useCallback(
    async ({ reloadIfCurrent = false } = {}) => {
      const registration = registrationRef.current;
      if (!registration) {
        if (reloadIfCurrent) window.location.reload();
        return reloadIfCurrent ? 'reloading' : 'unsupported';
      }
      if (registration.waiting) { applyUpdate(); return 'updated'; }
      const foundNew = await new Promise((resolve) => {
        let settled = false;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          registration.removeEventListener('updatefound', onUpdateFound);
          resolve(value);
        };
        function onUpdateFound() {
          const sw = registration.installing || registration.waiting;
          if (!sw) return;
          if (sw.state === 'installed' || registration.waiting) { finish(true); return; }
          sw.addEventListener('statechange', () => {
            if (sw.state === 'installed') finish(true);
            else if (sw.state === 'redundant') finish(false);
          });
        }
        registration.addEventListener('updatefound', onUpdateFound);
        registration.update().catch(() => finish(false));
        setTimeout(() => finish(false), EXPLICIT_CHECK_TIMEOUT_MS);
      });
      if (foundNew || registration.waiting) { applyUpdate(); return 'updated'; }
      if (reloadIfCurrent) { window.location.reload(); return 'reloading'; }
      return 'current';
    },
    [applyUpdate]
  );

  useEffect(() => {
    window.__esCheckForUpdate = checkForUpdate;
    const unregister = setUpdateChecker(checkForUpdate);
    return () => {
      unregister();
      if (window.__esCheckForUpdate === checkForUpdate) delete window.__esCheckForUpdate;
    };
  }, [checkForUpdate]);

  useEffect(() => {
    if (!needRefresh) return;
    // Arrived onto a stale shell (an update was already waiting) → just get onto latest.
    if (Date.now() - arrivedAt.current < ARRIVAL_AUTO_APPLY_MS) { applyUpdate(); return; }
    // In-session deploy → don't interrupt; offer it.
    setShowBanner(true);
  }, [needRefresh, applyUpdate]);

  // Announced once, when the banner first shows (showBanner never goes back to false).
  useEffect(() => {
    if (!showBanner) return undefined;
    setAnnouncement(t('update.available', 'A new version is available.'));
    const id = window.setTimeout(() => setAnnouncement(''), ANNOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [showBanner]); // not `t`: once, not again on a language switch

  // Detach records whether its Update button had focus; the remounted banner takes it back.
  const bannerRef = useCallback((el) => {
    if (!el) return undefined;
    showOnTop(el);
    if (refocusBanner.current) {
      refocusBanner.current = false;
      el.querySelector('button')?.focus();
    }
    return () => { refocusBanner.current = el.contains(document.activeElement); };
  }, []);

  const content = (
    <>
      <div className="visually-hidden" role="status" aria-live="polite">{announcement}</div>
      {showBanner && (
        <div key={modals.seq} ref={bannerRef} className="pwa-update-banner" popover="manual">
          <span>{t('update.available', 'A new version is available.')}</span>
          <button type="button" className="btn btn-sm btn-primary" onClick={applyUpdate}>
            {t('update.action', 'Update')}
          </button>
        </div>
      )}
    </>
  );
  return modals.top ? createPortal(content, modals.top) : content;
}
